import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Html } from '@react-three/drei';
import axios from 'axios';
import { API } from '../../config/api';

const http = axios.create({ baseURL: API.REDIS, timeout: 10000 });

// :5100 requires a token; `axios.create()` snapshots the global defaults, so one
// written later by `setAuthToken` never reaches this instance.
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-auth-token'] = token;
  }
  return config;
});

const COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32',
  GREY: '#808080', BLACK: '#333', WHITE: '#EEE', RED: '#EF4444',
  YELLOW: '#F59E0B', GREEN: '#22C55E', ORANGE: '#F97316'
};

// Order/stock destination badge text + colour from a group's `destination`
// (kind + enriched client names, attached by the queue endpoint).
function destLabelInfo(destination) {
  if (!destination) return null;
  const clients = [...new Set((destination.clients || [])
    .map(c => (typeof c === 'string' ? c : c?.name || ''))
    .filter(Boolean))];
  const kind = destination.kind || (clients.length ? 'ORDER' : 'STOCK');
  if (kind === 'STOCK') return { text: 'STOCK', color: '#9CA3AF' };
  if (kind === 'MIXED') return {
    text: clients.length ? `STOCK + ${clients.join(', ')}` : 'STOCK + ORDER',
    color: '#FBBF24'
  };
  return { text: clients.length ? clients.join(', ') : 'ORDER', color: '#4ADE80' };
}

function useActiveQueue(machineId) {
  return useQuery({
    queryKey: ['live-queue-3d', machineId],
    queryFn: async () => {
      const res = await http.get(`/api/redis/queues/${machineId}`);
      return res.data;
    },
    enabled: !!machineId,
    refetchInterval: 5000,
    staleTime: 3000
  });
}

// Extract active task's inputs and outputs from queue data
function getActiveIO(queueData) {
  if (!queueData?.groups?.length) return { inputs: [], outputs: [] };
  // Find the active group
  const activeGroup = queueData.groups.find(g => g.status === 'ACTIVE') || queueData.groups[0];
  if (!activeGroup?.queue?.length) return { inputs: [], outputs: [] };
  // Find the active task
  const activeTask = activeGroup.queue.find(t =>
    t.status === 'ACTIVE' || t.status === 'RUNNING'
  ) || activeGroup.queue[0];
  if (!activeTask) return { inputs: [], outputs: [] };

  return {
    inputs: (activeTask.inputs || []).map(t => ({ ...t, _product: activeGroup.productLabel, _phase: activeTask.phase })),
    outputs: (activeTask.outputs || []).map(t => ({ ...t, _product: activeGroup.productLabel, _phase: activeTask.phase })),
    product: activeGroup.productLabel,
    phase: activeTask.phase,
    destination: activeGroup.destination || null,
    taskStatus: activeTask.status,
    // Real-time: total produced by outputs = total consumed from inputs
    totalOutputProduced: (activeTask.outputs || []).reduce((s, o) => s + Number(o.producedLength || 0), 0),
    inputCount: (activeTask.inputs || []).length
  };
}

// Detailed floating label for a live drum on a machine
function LiveDrumTag({ position, token, role, consumedFromOutput = 0, isCoilingOutput = false, destination = null }) {
  if (!token) return null;
  const destInfo = destLabelInfo(destination);
  const avail = Math.round(token.availableLength || 0);
  const produced = Math.round(token.producedLength || 0);
  const planned = Math.round(token.plannedLength || 0);
  const isActive = token.status === 'ACTIVE' || token.status === 'RUNNING';
  const product = token._product || token.productLabel;
  const phase = role === 'INPUT' ? token.phase : (token._phase || token.phase);
  // Real-time remaining for INPUT: original available minus what output has consumed
  const realTimeRemaining = role === 'INPUT' ? Math.max(0, avail - Math.round(consumedFromOutput)) : 0;
  const consumed = role === 'INPUT' ? Math.round(consumedFromOutput) : 0;
  const borderColor = isActive ? '#22C55E' : '#3B82F6';
  const pct = planned > 0 ? Math.min(100, Math.round((produced / planned) * 100)) : 0;

  return (
    <Html position={position} center>
      <div style={{
        background: isActive ? 'rgba(5,20,10,0.95)' : 'rgba(5,10,20,0.92)',
        color: '#FFF', padding: '4px 7px',
        borderRadius: 4, fontSize: 8, fontFamily: 'monospace',
        border: `2px solid ${borderColor}`,
        boxShadow: isActive ? `0 0 8px ${borderColor}40` : 'none',
        userSelect: 'none', pointerEvents: 'none',
        whiteSpace: 'nowrap', minWidth: 75
      }}>
        {/* Role badge */}
        <div style={{
          fontSize: 7, fontWeight: 700, textTransform: 'uppercase',
          color: role === 'INPUT' ? '#F59E0B' : isCoilingOutput ? '#3B82F6' : '#22C55E',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span>{role === 'INPUT' ? 'PAY-OFF' : isCoilingOutput ? 'EUROPALLET' : 'TAKE-UP'}</span>
          {isActive && <span style={{ fontSize: 6, color: '#4ADE80', animation: 'pulse 1.5s infinite' }}>LIVE</span>}
        </div>
        {/* Product name */}
        {product && (
          <div style={{ fontSize: 7, color: '#CCC', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 90 }}>
            {product}
          </div>
        )}
        {/* Order/stock destination */}
        {destInfo && (
          <div title={destInfo.text} style={{
            fontSize: 7, fontWeight: 700, marginTop: 2, padding: '0 4px', borderRadius: 2,
            color: destInfo.color, background: `${destInfo.color}22`,
            border: `1px solid ${destInfo.color}55`,
            overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 90, whiteSpace: 'nowrap'
          }}>
            {destInfo.text}
          </div>
        )}
        {/* Color + meters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
          {token.color && (
            <div style={{ width: 6, height: 6, borderRadius: 1, background: COLOR_HEX[token.color] || '#888' }} />
          )}
          {role === 'INPUT' ? (
            <>
              <span style={{ fontWeight: 700, fontSize: 9, color: realTimeRemaining < avail ? '#F59E0B' : '#FFF' }}>
                {realTimeRemaining}m
              </span>
              <span style={{ fontSize: 7, color: '#888' }}>/ {avail}m</span>
            </>
          ) : (
            <>
              <span style={{ fontWeight: 700, fontSize: 9, color: isActive ? '#4ADE80' : '#FFF' }}>
                {produced}m
              </span>
              {planned > 0 && <span style={{ fontSize: 7, color: '#888' }}>/ {planned}m</span>}
            </>
          )}
        </div>
        {/* Consumption bar for input */}
        {role === 'INPUT' && consumed > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
            <div style={{ flex: 1, height: 3, background: '#333', borderRadius: 1 }}>
              <div style={{
                height: 3, borderRadius: 1,
                width: `${Math.min(100, avail > 0 ? Math.round((consumed / avail) * 100) : 0)}%`,
                background: '#F59E0B'
              }} />
            </div>
            <span style={{ fontSize: 7, color: '#F59E0B' }}>-{consumed}m</span>
          </div>
        )}
        {/* Progress bar for output */}
        {planned > 0 && role === 'OUTPUT' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
            <div style={{ flex: 1, height: 3, background: '#333', borderRadius: 1 }}>
              <div style={{
                height: 3, borderRadius: 1, width: `${pct}%`,
                background: isActive ? '#22C55E' : '#3B82F6'
              }} />
            </div>
            <span style={{ fontSize: 7, color: '#888' }}>{pct}%</span>
          </div>
        )}
        {/* Phase */}
        {phase && (
          <div style={{ fontSize: 6, color: '#666', marginTop: 1 }}>{phase}</div>
        )}
      </div>
      <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>
    </Html>
  );
}

// ─── HALL 1 LIVE DRUMS ─────────────────────────────────────
// Shows input/output tokens on active machines

export function Hall1LiveDrums({ machineData }) {
  const { data: l75Q } = useActiveQueue('l75');
  const { data: l120Q } = useActiveQueue('l120');
  const { data: dtbQ } = useActiveQueue('dtb1250');
  const { data: skipQ } = useActiveQueue('skip2250');

  const l75IO = useMemo(() => getActiveIO(l75Q), [l75Q]);
  const l120IO = useMemo(() => getActiveIO(l120Q), [l120Q]);
  const dtbIO = useMemo(() => getActiveIO(dtbQ), [dtbQ]);
  const skipIO = useMemo(() => getActiveIO(skipQ), [skipQ]);

  const RX = -6.5;
  const LX = -25.5;

  return (
    <group>
      {/* ── L75: 1 input → multiple outputs ─────────────── */}
      {l75IO.inputs[0] && (
        <LiveDrumTag position={[RX, 2, 43]} token={l75IO.inputs[0]} role="INPUT"
          consumedFromOutput={l75IO.totalOutputProduced} destination={l75IO.destination} />
      )}
      {l75IO.outputs.map((tok, i) => (
        <LiveDrumTag key={i} position={[RX, 2, i === 0 ? 15 : 11]} token={tok} role="OUTPUT" destination={l75IO.destination} />
      ))}

      {/* ── L120: 1 input → 1 output ────────────────────── */}
      {l120IO.inputs[0] && (
        <LiveDrumTag position={[LX, 2, 13]} token={l120IO.inputs[0]} role="INPUT"
          consumedFromOutput={l120IO.totalOutputProduced} destination={l120IO.destination} />
      )}
      {l120IO.outputs[0] && (
        <LiveDrumTag position={[LX, 2, 43]} token={l120IO.outputs[0]} role="OUTPUT" destination={l120IO.destination} />
      )}

      {/* ── DTB: N inputs → 1 output (shared consumption) ── */}
      {dtbIO.inputs.map((tok, i) => (
        <LiveDrumTag key={`dtb-in-${i}`} position={[LX - 1, 2, 64 + i * 2.5]} token={tok} role="INPUT"
          consumedFromOutput={dtbIO.inputCount > 0 ? dtbIO.totalOutputProduced / dtbIO.inputCount : 0} destination={dtbIO.destination} />
      ))}
      {dtbIO.outputs[0] && (
        <LiveDrumTag position={[LX, 2.5, 60]} token={dtbIO.outputs[0]} role="OUTPUT" destination={dtbIO.destination} />
      )}

      {/* ── Skip: N inputs → 1 output (shared consumption) ── */}
      {skipIO.inputs.map((tok, i) => (
        <LiveDrumTag key={`skip-in-${i}`} position={[RX - 1, 2, 67 + i * 3]} token={tok} role="INPUT"
          consumedFromOutput={skipIO.inputCount > 0 ? skipIO.totalOutputProduced / skipIO.inputCount : 0} destination={skipIO.destination} />
      ))}
      {skipIO.outputs[0] && (
        <LiveDrumTag position={[RX, 2.5, 50]} token={skipIO.outputs[0]} role="OUTPUT" destination={skipIO.destination} />
      )}
    </group>
  );
}

// ─── HALL 3 LIVE DRUMS ─────────────────────────────────────

export function Hall3LiveDrums({ machineData }) {
  const { data: achQ } = useActiveQueue('ach300');
  const { data: dhcQ } = useActiveQueue('dhc600');

  const achIO = useMemo(() => getActiveIO(achQ), [achQ]);
  const dhcIO = useMemo(() => getActiveIO(dhcQ), [dhcQ]);

  const WX = 33;

  return (
    <group>
      {/* ACH300: pay-off at Z=14, output (europallet) on machine Z=6 */}
      {achIO.inputs[0] && (
        <LiveDrumTag position={[WX, 2, 14]} token={achIO.inputs[0]} role="INPUT"
          consumedFromOutput={achIO.totalOutputProduced} destination={achIO.destination} />
      )}
      {achIO.outputs[0] && (
        <LiveDrumTag position={[WX, 2.5, 6]} token={achIO.outputs[0]} role="OUTPUT" isCoilingOutput destination={achIO.destination} />
      )}

      {/* DHC600: pay-off at Z=24, output (europallet) on machine Z=33 */}
      {dhcIO.inputs[0] && (
        <LiveDrumTag position={[WX, 2, 24]} token={dhcIO.inputs[0]} role="INPUT"
          consumedFromOutput={dhcIO.totalOutputProduced} destination={dhcIO.destination} />
      )}
      {dhcIO.outputs[0] && (
        <LiveDrumTag position={[WX, 2.5, 33]} token={dhcIO.outputs[0]} role="OUTPUT" isCoilingOutput destination={dhcIO.destination} />
      )}
    </group>
  );
}

// ─── HALL 4 LIVE DRUMS ─────────────────────────────────────

export function Hall4LiveDrums({ machineData }) {
  const { data: l70Q } = useActiveQueue('l70');
  const { data: athQ } = useActiveQueue('ath600');

  const l70IO = useMemo(() => getActiveIO(l70Q), [l70Q]);
  const athIO = useMemo(() => getActiveIO(athQ), [athQ]);

  const WX = 33;
  const LX = 13.5;

  return (
    <group>
      {/* L70: pay-off at Z=79, take-ups at Z=50,54 */}
      {l70IO.inputs[0] && (
        <LiveDrumTag position={[WX, 2, 79]} token={l70IO.inputs[0]} role="INPUT"
          consumedFromOutput={l70IO.totalOutputProduced} destination={l70IO.destination} />
      )}
      {l70IO.outputs.map((tok, i) => (
        <LiveDrumTag key={i} position={[WX, 2, i === 0 ? 50 : 54]} token={tok} role="OUTPUT" destination={l70IO.destination} />
      ))}

      {/* ATH600: pay-off at Z=70, take-up at Z=58 */}
      {athIO.inputs[0] && (
        <LiveDrumTag position={[LX, 2, 70]} token={athIO.inputs[0]} role="INPUT"
          consumedFromOutput={athIO.totalOutputProduced} destination={athIO.destination} />
      )}
      {athIO.outputs[0] && (
        <LiveDrumTag position={[LX, 2, 58]} token={athIO.outputs[0]} role="OUTPUT" destination={athIO.destination} />
      )}
    </group>
  );
}
